import { Button, Skeleton, Table, TableProps, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

// components
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import TableCustomCell from 'components/tableCustomCell/tableCustomCell';
import AuthActionComponent from 'components/authAction/authAction';
import { PlusOutlined } from '@ant-design/icons';
import NoData from 'components/noData/noData';
import Icon from 'utils/icon';

// services
import {
	onApproveIncompetent,
	onDeleteIncompetent,
	onGetIncompetents,
	onRejectIncompetent,
	onUnlinkIncompetent,
} from 'modules/incompetents/services/incompetents';
import NotificationService from 'core/services/notification';
import { isRTL } from 'core/services/language';

// models
import { Incompetent } from 'modules/incompetents/models/incompetents';
import { StatusColors } from 'utils/statusColors';

function ListIncompetentsPage() {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [incompetents, setIncompetents] = useState<Incompetent[]>([]);

	// pagination states
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [isLoadMore, setIsLoadMore] = useState(false);

	const columns: TableProps['columns'] = [
		{
			title: t('COMMON.ID'),
			dataIndex: 'id',
			key: 'id',
			render: (id) => <span style={{ fontWeight: 500, color: '#3F4254', textAlign: 'center', display: 'block' }}>{id}</span>,
		},
		{
			title: t('INCOMPETENTS.INCOMPETENT'),
			dataIndex: 'incompetent_name',
			key: 'incompetent_name',
			render: (incompetent_name, record) => (
				<Link to={`/users/view/${record?.incompetent_id}`} style={{ textDecoration: 'none' }}>
					<TableCustomCell title={isRTL() ? incompetent_name?.ar : incompetent_name?.en} subTitle={record.incompetent_national_id} />
				</Link>
			),
		},
		{
			title: t('INCOMPETENTS.GARDIAN'),
			dataIndex: 'guardian_name',
			key: 'guardian_name',
			render: (guardian_name, record) => (
				<Link to={`/users/view/${record?.guardian_id}`} style={{ textDecoration: 'none' }}>
					<TableCustomCell title={isRTL() ? guardian_name?.ar : guardian_name?.en} subTitle={record.guardian_national_id} />
				</Link>
			),
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status_key',
			key: 'status',
			render: (status_key) => <span className={`status ${StatusColors[status_key]} `}>{t(`MINORS.${status_key?.toUpperCase()}`)}</span>,
		},
		{
			title: t('INCOMPETENTS.FAMILY_ID_DOCUMENT'),
			dataIndex: 'family_id_document',
			key: 'family_id_document',
			render: (family_id_document) => (
				<Button
					type="primary"
					style={{ padding: '3px 7px' }}
					className="d-block m-auto"
					onClick={() => downloadFile(family_id_document, 'family_id_document.pdf')}>
					{t('COMMON.DOWNLOAD')}
				</Button>
			),
		},
		{
			title: t('INCOMPETENTS.CUSTODY_ORDER_DOCUMENT'),
			dataIndex: 'custody_order_document',
			key: 'custody_order_document',
			render: (custody_order_document) => (
				<Button
					type="primary"
					style={{ padding: '3px 7px' }}
					className="d-block m-auto"
					onClick={() => downloadFile(custody_order_document, 'custody_order_document.pdf')}>
					{t('COMMON.DOWNLOAD')}
				</Button>
			),
		},
		{
			title: t('INCOMPETENTS.GUARDIANSHIP_ORDER_DOCUMENT'),
			dataIndex: 'guardianship_order_document',
			key: 'guardianship_order_document',
			render: (guardianship_order_document) => (
				<Button
					type="primary"
					style={{ padding: '3px 7px' }}
					className="d-block m-auto"
					onClick={() => downloadFile(guardianship_order_document, 'guardianship_order_document.pdf')}>
					{t('COMMON.DOWNLOAD')}
				</Button>
			),
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '',
			key: '',
			render: (_, minor) => (
				<div className="d-flex gap-2">
					<AuthActionComponent action="minor_view">
						<Link to={`${minor.id}`}>
							<Button type="primary" className="discard" style={{ paddingBlock: '6px' }}>
								{t('COMMON.VIEW')}
							</Button>
						</Link>
					</AuthActionComponent>
				</div>
			),
		},
	];

	useEffect(() => {
		getIncompetents();
	}, []);

	const getIncompetents = async (page?: number) => {
		try {
			setIsLoading(true);

			const response = await onGetIncompetents(page);
			setIncompetents(response.data.data.incompetents);
			setTotalRecords(response.data.data.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			setIsLoadMore(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });
			const response = await onGetIncompetents(page);
			setIncompetents(response.data.data.incompetents);
			setCurrentPage(page);
			setIsLoadMore(false);
		} catch (error) {
			setIsLoadMore(false);
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const downloadFile = (url: string, filename: string) => {
		fetch(url)
			.then((response) => response.blob())
			.then((blob) => {
				const link = document.createElement('a');
				link.href = URL.createObjectURL(blob);
				link.download = filename;
				document.body.appendChild(link);
				link.click();
				link.remove();
				URL.revokeObjectURL(link.href);
			})
			.catch((err) => console.error('Download failed:', err));
	};

	return (
		<>
			<div className="pageHeader">
				<h3>{t('INCOMPETENTS.INCOMPETENTS')}</h3>
			</div>

			<>
				<div className={`sectionContainer listData`}>
					<header>
						<div>
							<h5 className="d-flex align-items-center gap-2">
								{isLoading ? <Skeleton.Node active style={{ width: 40, height: 24 }} /> : totalRecords} {t('INCOMPETENTS.INCOMPETENTS')}
							</h5>
							<p>{t('INCOMPETENTS.MANAGEMENT_DESCRIPTION')}</p>
						</div>
					</header>

					{isLoading ? (
						<LoadingComponent />
					) : (
						<div className="position-relative">
							<Table
								rowKey="id"
								columns={columns}
								dataSource={incompetents}
								scroll={{ x: 400 }}
								pagination={{
									pageSize: 15,
									total: totalRecords,
									showSizeChanger: false,
									current: currentPage,
									onChange: fetchMoreData,
								}}
								locale={{ emptyText: <NoData /> }}
							/>
							{isLoadMore && (
								<div className="loadMoreContainer">
									<LoadingComponent />
								</div>
							)}
						</div>
					)}
				</div>
			</>
		</>
	);
}

export default ListIncompetentsPage;
